import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, FileText, ExternalLink, ChevronRight } from 'lucide-react';

const FAQS = [
  {
    q: "How do I install the Chrome Extension?",
    a: "Visit your account → Clipper Setup → click \"Download Extension\" → unzip → open chrome://extensions → enable Developer mode → click \"Load unpacked\" → select the unzipped folder. Once we're approved on the Chrome Web Store, install will be a single click."
  },
  {
    q: "Which listing sites does the extension support?",
    a: "27 real estate sites including Zillow, Realtor.com, Redfin, Trulia, Compass, Coldwell Banker, Sotheby's, KW, Century 21, RE/MAX, Movoto, Homes.com, HAR, Homesnap, Estately, OneHome, FlexMLS, Realtor.ca, Royal LePage, REW, Zolo, HouseSigma, Point2Homes, Condos.ca, and more."
  },
  {
    q: "The extension says \"Cannot reach backend\"",
    a: "Open the extension popup → check the API endpoint shows https://realprop.io/api → click Save. If the issue persists, make sure you're signed in to RealProp in the same browser."
  },
  {
    q: "My listing didn't import all the photos / details",
    a: "Some sites lazy-load photos as you scroll. Try scrolling through the full photo gallery on the listing page before clicking the orb. If a field is still missing, our AI Vision fallback runs automatically and fills in the gaps."
  },
  {
    q: "How do I cancel my subscription?",
    a: "Go to More → Subscription → Cancel. Your access continues until the end of the current billing period. No questions asked."
  },
  {
    q: "How do I delete my account & data?",
    a: "Visit /data-deletion or email support@realprop.io. We delete everything within 30 days."
  },
];

export const SupportPage = () => {
  return (
    <div
      className="min-h-screen bg-[#0B0C10] text-[#F8F9FA]"
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="support-page"
    >
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0B0C10]/80 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="support-logo">
            RealProp
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#8A8D98] hover:text-white transition-colors"
            data-testid="support-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-screen-md mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A8D98] mb-3">
          <span style={{ color: '#E3A869' }}>●</span>&nbsp;&nbsp;Support
        </div>
        <h1
          className="text-5xl sm:text-6xl font-light leading-[0.95] tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="support-title"
        >
          We're <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', color: '#E3A869' }}>here</span>.
        </h1>
        <p className="mt-5 text-sm text-[#8A8D98] max-w-md">
          Real estate marketing — done right. Questions, bug reports, feature ideas — we read every message.
        </p>

        {/* Contact cards */}
        <section className="mt-12 grid sm:grid-cols-2 gap-3">
          <a
            href="mailto:support@realprop.io"
            className="group p-5 rounded-2xl border border-white/10 hover:border-[#E3A869]/60 hover:bg-white/[0.02] transition-all"
            data-testid="contact-email"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-[#E3A869]/40 transition-colors">
                <Mail className="h-4 w-4 text-[#E3A869]" strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Email support</p>
                <p className="text-xs text-[#8A8D98] mt-0.5">support@realprop.io</p>
              </div>
              <ChevronRight className="h-4 w-4 text-[#8A8D98] group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </div>
            <p className="text-[11px] text-[#5b5e68] mt-3 leading-relaxed">Avg. response within 24 hours, Mon–Fri.</p>
          </a>

          <a
            href="https://realprop.io"
            className="group p-5 rounded-2xl border border-white/10 hover:border-[#E3A869]/60 hover:bg-white/[0.02] transition-all"
            data-testid="contact-website"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-[#E3A869]/40 transition-colors">
                <MessageSquare className="h-4 w-4 text-[#E3A869]" strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">In-app chat</p>
                <p className="text-xs text-[#8A8D98] mt-0.5">Sign in → bottom-right widget</p>
              </div>
              <ChevronRight className="h-4 w-4 text-[#8A8D98] group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </div>
            <p className="text-[11px] text-[#5b5e68] mt-3 leading-relaxed">Fastest for active subscribers.</p>
          </a>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] mb-6 font-semibold">
            Frequently asked
          </div>
          <div className="space-y-5">
            {FAQS.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-white/10 p-5 hover:border-white/20 transition-colors"
                data-testid={`faq-${i}`}
              >
                <summary
                  className="cursor-pointer flex items-center justify-between gap-4 text-sm font-medium text-white list-none"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <span>{f.q}</span>
                  <ChevronRight className="h-4 w-4 text-[#8A8D98] group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <p className="mt-3 text-sm text-[#8A8D98] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section className="mt-16">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#8A8D98] mb-6 font-semibold">
            Resources
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Link
              to="/privacy"
              className="group flex items-center gap-3 p-4 rounded-xl border border-white/10 hover:border-white/30 transition-colors"
              data-testid="resource-privacy"
            >
              <FileText className="h-4 w-4 text-[#8A8D98] group-hover:text-[#E3A869] transition-colors" />
              <span className="text-sm">Privacy policy</span>
            </Link>
            <Link
              to="/terms"
              className="group flex items-center gap-3 p-4 rounded-xl border border-white/10 hover:border-white/30 transition-colors"
              data-testid="resource-terms"
            >
              <FileText className="h-4 w-4 text-[#8A8D98] group-hover:text-[#E3A869] transition-colors" />
              <span className="text-sm">Terms of service</span>
            </Link>
            <Link
              to="/data-deletion"
              className="group flex items-center gap-3 p-4 rounded-xl border border-white/10 hover:border-white/30 transition-colors"
              data-testid="resource-deletion"
            >
              <FileText className="h-4 w-4 text-[#8A8D98] group-hover:text-[#E3A869] transition-colors" />
              <span className="text-sm">Delete my data</span>
            </Link>
          </div>
        </section>

        <p className="text-center text-[11px] text-[#3C3D43] mt-20" style={{ fontFamily: 'Playfair Display, serif' }}>
          RealProp · Marketing on autopilot.
        </p>
      </main>
    </div>
  );
};

export default SupportPage;
